import { useEffect, useId, useMemo, useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * Minimal time-series line chart (SVG, no dependencies).
 *
 * - One y-axis; categorical colours in fixed order (theme `--chart-1..5`), never
 *   cycled: series beyond five are listed in the table only.
 * - Crosshair + tooltip listing every series at the hovered time.
 * - A legend for two or more series, and a table view, so identity never relies on colour.
 */

export interface ChartSeries {
  key: string;
  label: string;
  points: { t: number; v: number | null }[];
}

const DEFAULT_W = 800;
const H = 260;
const PAD = { top: 12, right: 12, bottom: 28, left: 56 };
export const MAX_COLORED_SERIES = 5;

const color = (i: number): string => `var(--chart-${i + 1})`;

function niceTicks(min: number, max: number, count = 4): number[] {
  if (min === max) {
    const d = Math.abs(min) || 1;
    min -= d / 2;
    max += d / 2;
  }
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  // Always extend one tick past the maximum so no line leaves the plot.
  for (let v = Math.floor(min / step) * step; out.length < 50; v += step) {
    out.push(Number(v.toPrecision(12)));
    if (v >= max - step * 1e-9) break;
  }
  return out;
}

export function formatValue(v: number): string {
  const a = Math.abs(v);
  if (a === 0) return '0';
  if (a >= 1e9) return `${(v / 1e9).toFixed(2)}G`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e4) return `${(v / 1e3).toFixed(1)}k`;
  if (a >= 100) return v.toFixed(0);
  if (a >= 1) return v.toFixed(2);
  return v.toPrecision(3);
}

function timeLabel(t: number, spanSec: number): string {
  const iso = new Date(t * 1000).toISOString();
  return spanSec > 2 * 86_400 ? iso.slice(5, 10) : iso.slice(11, 16);
}

export function LineChart({ series, title }: { series: ChartSeries[]; title: string }) {
  const { t: tr } = useTranslation('ops');
  const id = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const plotRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  // The viewBox follows the rendered width so tick labels are never stretched.
  const [W, setW] = useState(DEFAULT_W);
  useEffect(() => {
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry?.contentRect.width ?? 0);
      if (w > 0) setW(Math.max(320, w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const colored = useMemo(() => series.slice(0, MAX_COLORED_SERIES), [series]);

  const geo = useMemo(() => {
    const times = [...new Set(colored.flatMap((s) => s.points.map((p) => p.t)))].sort((a, b) => a - b);
    const values = colored.flatMap((s) => s.points.map((p) => p.v)).filter((v): v is number => v !== null && Number.isFinite(v));
    const t0 = times[0] ?? 0;
    const t1 = times[times.length - 1] ?? t0 + 1;
    const lo = Math.min(0, ...values);
    const hi = values.length ? Math.max(...values) : 1;
    const ticks = niceTicks(lo, hi);
    const yMin = ticks[0] ?? lo;
    const yMax = ticks[ticks.length - 1] ?? hi;
    const x = (t: number) => PAD.left + ((t - t0) / Math.max(1, t1 - t0)) * (W - PAD.left - PAD.right);
    const y = (v: number) => H - PAD.bottom - ((v - yMin) / Math.max(1e-12, yMax - yMin)) * (H - PAD.top - PAD.bottom);
    return { times, t0, t1, ticks, x, y };
  }, [colored, W]);

  const paths = colored.map((s) => {
    let d = '';
    let pen = false;
    for (const p of s.points) {
      if (p.v === null || !Number.isFinite(p.v)) {
        pen = false;
        continue;
      }
      d += `${pen ? 'L' : 'M'}${geo.x(p.t).toFixed(1)},${geo.y(p.v).toFixed(1)}`;
      pen = true;
    }
    return d;
  });

  const onMove = (e: PointerEvent<SVGRectElement>) => {
    const svg = svgRef.current;
    if (!svg || geo.times.length === 0) return;
    const r = svg.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    let best = 0;
    for (let i = 1; i < geo.times.length; i++) {
      if (Math.abs(geo.x(geo.times[i] ?? 0) - px) < Math.abs(geo.x(geo.times[best] ?? 0) - px)) best = i;
    }
    setHover(best);
  };

  const ht = hover !== null ? geo.times[hover] : undefined;
  const span = geo.t1 - geo.t0;
  const xTicks = geo.times.length ? [0, 0.25, 0.5, 0.75, 1].map((f) => geo.t0 + f * span) : [];
  const tooltipLeft = ht !== undefined ? (geo.x(ht) / W) * 100 : 0;

  return (
    <figure className="m-0 space-y-3" aria-labelledby={`${id}-cap`}>
      <figcaption id={`${id}-cap`} className="sr-only">
        {title}
      </figcaption>
      <div className="relative" ref={plotRef}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title} className="block w-full overflow-visible" style={{ height: H }}>
          {geo.ticks.map((t) => (
            <g key={t}>
              <line className="stroke-border" strokeWidth={1} vectorEffect="non-scaling-stroke" x1={PAD.left} x2={W - PAD.right} y1={geo.y(t)} y2={geo.y(t)} />
              <text className="fill-muted-foreground text-[11px]" x={PAD.left - 6} y={geo.y(t) + 4} textAnchor="end">
                {formatValue(t)}
              </text>
            </g>
          ))}
          {xTicks.map((t, i) => (
            <text key={i} className="fill-muted-foreground text-[11px]" x={geo.x(t)} y={H - 8} textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}>
              {timeLabel(t, span)}
            </text>
          ))}
          {paths.map((d, i) => (
            <path key={colored[i]?.key ?? i} d={d} fill="none" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" style={{ stroke: color(i) }} />
          ))}
          {ht !== undefined ? (
            <>
              <line className="stroke-muted-foreground" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" x1={geo.x(ht)} x2={geo.x(ht)} y1={PAD.top} y2={H - PAD.bottom} />
              {colored.map((s, i) => {
                const p = s.points.find((q) => q.t === ht);
                return p && p.v !== null ? (
                  <circle key={s.key} cx={geo.x(ht)} cy={geo.y(p.v)} r={4} className="stroke-card" strokeWidth={2} style={{ fill: color(i) }} />
                ) : null;
              })}
            </>
          ) : null}
          <rect
            x={PAD.left}
            y={PAD.top}
            width={W - PAD.left - PAD.right}
            height={H - PAD.top - PAD.bottom}
            fill="transparent"
            onPointerMove={onMove}
            onPointerLeave={() => setHover(null)}
          />
        </svg>
        {ht !== undefined ? (
          <div
            className={cn('pointer-events-none absolute top-2 z-10 max-w-[min(28rem,80%)] space-y-1 rounded-md border bg-popover p-2 text-xs text-popover-foreground shadow-md', tooltipLeft > 60 ? '-translate-x-full -ml-3' : 'ml-3')}
            style={{ left: `${tooltipLeft}%` }}
            role="status"
          >
            <div className="font-mono text-muted-foreground">{new Date(ht * 1000).toISOString().replace('T', ' ').slice(0, 19)} UTC</div>
            {colored.map((s, i) => {
              const p = s.points.find((q) => q.t === ht);
              return (
                <div key={s.key} className="flex items-center gap-2">
                  <span className="size-2.5 shrink-0 rounded-sm" style={{ background: color(i) }} aria-hidden="true" />
                  <strong className="tabular">{p && p.v !== null ? formatValue(p.v) : '—'}</strong>
                  <span className="truncate font-mono text-muted-foreground">{s.label}</span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
      {colored.length > 1 ? (
        <ul className="flex flex-wrap gap-x-4 gap-y-1">
          {colored.map((s, i) => (
            <li key={s.key} className="flex min-w-0 items-center gap-1.5">
              <span className="size-2.5 shrink-0 rounded-sm" style={{ background: color(i) }} aria-hidden="true" />
              <span className="font-mono text-xs break-all">{s.label}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {series.length > MAX_COLORED_SERIES ? (
        <p className="text-xs text-muted-foreground">{tr('chart.truncated', { shown: MAX_COLORED_SERIES, total: series.length })}</p>
      ) : null}
    </figure>
  );
}
